import React, { Dispatch, useEffect } from 'react';
import { Box, Checkbox, Drawer, FormControl, IconButton, Stack } from '@mui/material';
import { VigoTypography } from 'src/@vigo/vigo-typography';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { Controller, useForm } from 'react-hook-form';
import { VigoTextInput } from 'src/@vigo/vigo-textinput';
import { DynamicComponent } from 'src/@fake-db/dashboard';
import { VigoButton } from 'src/@vigo/vigo-button';
import { widget } from './customDashboard/types';

interface ComponentEditDrawerProps {
  open: boolean;
  setOpen: Dispatch<boolean>;
  widget?: widget;
}

const defaultComponentStructure = {
  i: '',
  x: 0,
  y: 0,
  w: 0,
  h: 0,
  type: '',
  title: '',
  static: false,
  isBound: false,
  href: '',
  componentProps: {
    serviceLocation: '',
    endpoint: '',
    type: undefined,
    title: '',
    iconColor: '',
    countColor: '',
    titleColor: ''
  }
};

const ComponentEditDrawer = ({ open, setOpen, widget }: ComponentEditDrawerProps) => {
  const { control, reset, handleSubmit } = useForm<DynamicComponent>({ defaultValues: defaultComponentStructure });

  // const { vigoToast } = useVigoToast({});

  const onSubmit = (_data: DynamicComponent) => {
    // Need to handle saving component information

    return;
  };

  const joinPath = <Path extends string, Key extends string>(path: Path, key: Key) =>
    `${path}.${key}` as `${Path}.${Key}`;

  useEffect(() => {
    reset(widget);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widget]);

  return (
    <Drawer open={open} hideBackdrop anchor="right" sx={{ zIndex: '19999' }}>
      <Box
        className="customizer-header"
        sx={{
          position: 'relative',
          p: theme => theme.spacing(3.5, 5),
          borderBottom: theme => `1px solid ${theme.palette.divider}`,
          display: 'flex',
          justifyContent: 'space-between'
        }}>
        <VigoTypography
          value="Component Configuration"
          variant="h6"
          sx={{ fontWeight: 600, textTransform: 'uppercase' }}
        />
        <IconButton
          onClick={() => setOpen(false)}
          sx={{
            color: 'text.secondary'
          }}>
          <CloseOutlinedIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box className="customizer-body" sx={{ width: '400px' }}>
        {widget && (
          <form onSubmit={handleSubmit(onSubmit)} style={{ padding: '20px' }}>
            <FormControl fullWidth sx={{ marginBottom: '20px' }}>
              <Controller
                name="title"
                control={control}
                render={({ field }) => <VigoTextInput {...field} label="Title" />}
              />
            </FormControl>
            <FormControl fullWidth sx={{ marginBottom: '20px' }}>
              <Controller
                name="type"
                control={control}
                render={({ field }) => (
                  <VigoTextInput {...field} label="Component Type" inputProps={{ readOnly: true }} />
                )}
              />
            </FormControl>

            <FormControl fullWidth sx={{ marginBottom: '20px' }}>
              <Controller
                name="href"
                control={control}
                render={({ field }) => <VigoTextInput {...field} label="href" />}
              />
            </FormControl>

            <Stack direction="column">
              <FormControl>
                <Controller
                  name="static"
                  control={control}
                  render={({ field }) => (
                    <Stack direction="row" alignItems="center">
                      <VigoTypography value="Static:" sx={{ width: '80px' }} />
                      <Checkbox {...field} checked={field.value} sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }} />
                    </Stack>
                  )}
                />
              </FormControl>
              <FormControl>
                <Controller
                  name="isBound"
                  control={control}
                  render={({ field }) => (
                    <Stack direction="row" alignItems="center">
                      <VigoTypography value="Is Bound:" sx={{ width: '80px' }} />
                      <Checkbox {...field} checked={field.value} sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }} />
                    </Stack>
                  )}
                />
              </FormControl>

              {widget?.componentProps && Object.keys(widget.componentProps).length > 0 && (
                <Stack direction="column" gap="15px" marginBottom="20px">
                  <VigoTypography value="Component Properties" />
                  {Object.keys(widget.componentProps)
                    .map((prop, index) => ({ id: index, name: joinPath('componentProps', prop), label: prop }))
                    .map((propObj: { id: number; name: any; label: string }) => (
                      <FormControl fullWidth sx={{ marginBottom: '20px' }} key={propObj.id}>
                        <Controller
                          name={propObj.name}
                          control={control}
                          render={({ field }) => (
                            <VigoTextInput
                              {...field}
                              type={propObj.name.includes('Color') ? 'color' : 'text'}
                              sx={{ width: propObj.name.includes('Color') ? '35%' : '100%' }}
                              label={propObj.label}
                            />
                          )}
                        />
                      </FormControl>
                    ))}
                </Stack>
              )}
            </Stack>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
              <VigoButton name="update" value="Update" variant="contained" type="submit" />
            </Box>
          </form>
        )}
      </Box>
    </Drawer>
  );
};

export default ComponentEditDrawer;
